/**
 * Holds the `via` tag an expert link arrived with, so the expert link screen can report
 * which platform the link was followed from.
 *
 * The API adds the tag to the link a preview card opens (`/ask/<slug>?via=linkedin`).
 * Like any link parameter no route declares, it is readable at entry and not reliably
 * after: the router rebuilds the address bar from its own state once it resolves the
 * route. On native a universal link hands the app the URL without the page ever loading,
 * so entry is the only place the tag is seen at all.
 *
 * Keyed to the slug it arrived on and one-shot, like `communicationAttribution.ts`: a
 * later visit to the same expert from the directory must not inherit a click source.
 */

const EXPERT_LINK_PATH = /^\/?ask\/([a-z0-9-]+)\/?$/;

/** Registry ids are lowercase words joined by underscores; anything else is not a tag. */
const VIA_TAG = /^[a-z][a-z_]{0,39}$/;

let pending: { slug: string; via: string } | null = null;

/** The value as a platform tag, or undefined when it is not shaped like one. */
export function readViaTag(value: string | undefined): string | undefined {
  return value && VIA_TAG.test(value) ? value : undefined;
}

/**
 * Records the tag an entry arrived with. An entry that is not an expert link is ignored,
 * so ordinary navigation cannot displace a tag still waiting for its screen, while an
 * untagged expert link clears a stale one.
 */
export function recordExpertLinkEntry(path: string, via: string | undefined): void {
  const slug = EXPERT_LINK_PATH.exec(path)?.[1];

  if (!slug) {
    return;
  }

  const tag = readViaTag(via);

  pending = tag ? { slug, via: tag } : null;
}

/** The tag for this slug's entry, handed over once. */
export function takeExpertLinkVia(slug: string): string | undefined {
  const entry = pending;

  if (!entry || entry.slug !== slug) {
    return undefined;
  }

  pending = null;

  return entry.via;
}

/** Test seam. Clears the held tag so one test's entry cannot attribute another's screen. */
export function clearExpertLinkEntry(): void {
  pending = null;
}
